import { brand } from "@/branding/theme";
import { Leaf } from "lucide-react";
import { useState } from "react";

/**
 * TrueRoots Logo — single source of truth.
 * If `brand.logo.includesWordmark` is true, the file already contains the
 * wordmark so we hide our React-rendered text to avoid duplication.
 *
 * Sizes are chosen to display the raster mark crisply at 2x DPR.
 */
export default function Logo({ variant = "light", size = "md", wordmark = true, testid = "brand-logo" }) {
  const src = variant === "dark" ? brand.logo.srcDark : brand.logo.src;
  const [failed, setFailed] = useState(false);

  const heights = { sm: "h-8", md: "h-10", lg: "h-12", xl: "h-16" };
  const iconBox = { sm: "w-8 h-8", md: "w-9 h-9", lg: "w-12 h-12", xl: "w-16 h-16" };
  const iconSize = { sm: "w-4 h-4", md: "w-4 h-4", lg: "w-6 h-6", xl: "w-8 h-8" };
  const textSize = { sm: "text-base", md: "text-lg", lg: "text-lg", xl: "text-2xl" };

  // The official brand raster typically already contains the wordmark. If the
  // brand config declares that, we suppress the React-rendered wordmark to
  // avoid a duplicate. Otherwise we render both so "logo + name" is complete.
  const showText = wordmark && !brand.logo.includesWordmark;

  if (!failed) {
    return (
      <span className={`inline-flex items-center gap-2 ${heights[size]}`} data-testid={testid}>
        <img
          src={src}
          alt={`${brand.name} logo`}
          className={`${heights[size]} w-auto object-contain block`}
          onError={() => setFailed(true)}
          loading="eager"
          decoding="async"
        />
        {showText && (
          <span className={`font-display font-extrabold tracking-tight ${textSize[size]}`}>
            TrueRoots<span className="text-[var(--tr-zest)]">.</span>
            <span className="font-semibold">One</span>
          </span>
        )}
      </span>
    );
  }

  // Fallback until an asset is present — leaf mark placeholder.
  return (
    <span className="inline-flex items-center gap-2" data-testid={testid}>
      <span
        className={`${iconBox[size]} rounded-full flex items-center justify-center ${
          variant === "dark" ? "bg-[var(--tr-citrus-yellow)]" : "bg-[var(--tr-deep-green)]"
        }`}
        aria-hidden
      >
        <Leaf
          className={`${iconSize[size]} ${
            variant === "dark" ? "text-[var(--tr-deep-green)]" : "text-[var(--tr-citrus-yellow)]"
          }`}
          strokeWidth={2.5}
        />
      </span>
      {wordmark && (
        <span className={`font-display font-extrabold tracking-tight ${textSize[size]}`}>
          TrueRoots<span className="text-[var(--tr-zest)]">.</span>
          <span className="font-semibold">One</span>
        </span>
      )}
    </span>
  );
}
